import React, { useContext } from 'react';
import isNull from 'lodash/isNull';
import groupBy from 'lodash/groupBy';
import isUndefined from 'lodash/isUndefined';
import union from '@turf/union';
import { scaleOrdinal } from 'd3-scale';
import { format } from 'd3-format';
import { sum } from 'd3-array';
// import Warning from './Warning';
import { FarmContext, GEO_URL } from './FarmContext';
import { AppContext } from './AppContext';
import Table from './Table';
import { getArea } from './geo';

const dataProp = 'musym';


export const SoilTable = ({ ssurgoMapunits }) => {
  if (!ssurgoMapunits) return null;
  const headers = ['muname', 'Corn NCCPI', 'Corn non irr. yield', 'IA CSR', 'PI', 'acres', '%'];
  const rows = Object.entries(groupBy(ssurgoMapunits.features, f => f.properties['mukey']))
    .map(([key, values]) => {
      const acres = sum(values, f => getArea(f.geometry));
      return {
        // should all be the same
        'muname': values[0].properties['muname'].split(',')[0],
        'musym': values[0].properties['musym'],
        'Corn NCCPI': format('.1f')(values[0].properties['interphr'] * 100),
        'Corn non irr yld': values[0].properties['nonirryield_r'],
        'IA CSR': values[0].properties['iacornsr'],
        'PI': values[0].properties['component_cropprodindex'],
        'acres': format('.1f')(acres),
        'unformated_area': acres
      }
    })

  const total = sum(rows, r => r.unformated_area);
  rows.forEach(r => {
    r['%'] = format('.0%')(r.unformated_area / total)
  })

  rows.sort((a, b) => b.unformated_area - a.unformated_area);

  const weightedAvg = (rows, k, wk) => {
    const valid = rows.filter(r => r[k]);
    const avg = sum(valid, v => v[k] * v[wk]) / sum(valid, v => v[wk]);
    if (!avg) return '';
    return format('.1f')(avg);
  }

  rows.unshift({
    'muname': 'AVG',
    'Corn NCCPI': weightedAvg(rows, 'Corn NCCPI', 'unformated_area'),
    'Corn non irr yld': weightedAvg(rows, 'Corn non irr yld', 'unformated_area'),
    'IA CSR': weightedAvg(rows, 'IA CSR', 'unformated_area'),
    'PI': weightedAvg(rows, 'PI', 'unformated_area'),
    '%': format('.0%')(1)
  })

  const colorFunc = getColorFunc(dataProp, ssurgoMapunits);
  const getColor = (r, h) => {
    if (h === 'muname' && r[h] === 'AVG') return {color: 'black'};
    if (dataProp && ('muname' === h)) return { color: colorFunc(r[dataProp]) };
    return null;
  }

  return <Table
    headers={headers}
    rows={rows}
    getColor={getColor}
  />
}


const notNull = v => !(isUndefined(v) || isNull(v));


const getColorFunc = (dataProp, fc) => {
  const values = fc.features.map(f => f.properties[dataProp]);
  const Tableau10 = ["#4e79a7","#f28e2c","#e15759","#76b7b2","#59a14f","#edc949","#af7aa1","#ff9da7","#9c755f","#bab0ab"];
  const domain = Array.from(new Set(values)).filter(notNull);
  const range = Tableau10;
  const colorFunc = scaleOrdinal(domain, range);
  return colorFunc;
}


const categoricalPaint = (dataProp, fc) => {
  const colorFunc = getColorFunc(dataProp, fc);

  const ll = [
    "match",
    ["get", dataProp],
  ]
  colorFunc.domain().forEach(v => {
    ll.push([v])
    ll.push(colorFunc(v))
  })
  // default
  ll.push('lightgrey')
  return ll
}

export const getSsurgo = (boundary) => {

  const combined = boundary.features.reduce((acc, cur) => {
    return union(acc, cur);
  });
  return fetch(GEO_URL + '/geoserver/ssurgo_mapunits', {
    method: 'POST',
    body: JSON.stringify({boundary: combined}),
    headers: {
      'Content-Type': 'application/json; charset=utf-8'
    }
  }).then(r => r.json())
}

const getSoilLayers = (ssurgoMapunits) => {
  if (!ssurgoMapunits) return [];
  if (ssurgoMapunits.features.length === 0) return [];

  const layerId = "ssurgoMapunits";
  const ssurgoLayerLabels = {
    id: layerId + 'labels',
    type: 'symbol',
    layout: {
      'text-field': ['format', ['get', 'muname'], ' (', ['get', 'musym'], ')']
    },
    paint: {
      'text-color': categoricalPaint(dataProp, ssurgoMapunits),
      'text-halo-color': 'black',
      'text-halo-width': 0.5,
    },
    source: {
      type: 'geojson',
      data: ssurgoMapunits,
    }
  };

  const ssurgoLayer = {
    id: layerId,
    type: 'fill',
    source: {
      type: 'geojson',
      data: ssurgoMapunits,
    },
    paint: {
      'fill-color': categoricalPaint(dataProp, ssurgoMapunits),
      'fill-outline-color': categoricalPaint(dataProp, ssurgoMapunits),
      'fill-opacity': 0.5
    }
  };

  return [ssurgoLayer, ssurgoLayerLabels];
}

export const updateSoilLayer = (mapboxRef, ssurgoMapunits, toggle) => {
    if (!mapboxRef.current) return;
    const layers = getSoilLayers(ssurgoMapunits);
    layers.forEach(l => {
      if (mapboxRef.current.getLayer(l.id)) mapboxRef.current.removeLayer(l.id);
      if (mapboxRef.current.getSource(l.id)) mapboxRef.current.removeSource(l.id);
      if (toggle) mapboxRef.current.addLayer(l);
    });
}

export const useSoilToggle = () => {
  const { ssurgoMapunits } = useContext(FarmContext);
  const { mapboxRef } = useContext(AppContext);
  
  const soilToggle = (toggle) => {
    updateSoilLayer(mapboxRef, ssurgoMapunits, toggle);
  }

  return soilToggle;
}
